'use client';

import {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useContext,
  type FC,
  ReactNode,
} from 'react';
import { Alert, Button, ButtonGroup, Dialog, Flex } from '@/_components';
import { sendClientLoginInfo } from '@/_api/advisors';
import { getAdminUsersByIds } from '@/_api/v2/admin-users';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type {
  SendSignInLinksDialogProps,
  ClientMapItem,
} from './SendSignInLinksDialog.types';

export const SendSignInLinksDialog: FC<SendSignInLinksDialogProps> = ({
  isOpen,
  onClose,
  selectedClientIds,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [clientsMap, setClientsMap] = useState<Array<ClientMapItem>>([]);

  const selectedClient: ClientMapItem | undefined = clientsMap?.find(
    (client: ClientMapItem) =>
      client.id.toString() === selectedClientIds[0].toString(),
  );

  const isSingleClient: boolean = selectedClientIds?.length === 1;

  const onSendNow = async () => {
    try {
      setIsLoading(true);

      const userIds: Array<number> =
        isSingleClient && selectedClient?.id
          ? [selectedClient.id]
          : selectedClientIds;

      const { error } = await sendClientLoginInfo({
        userIds,
        token,
      });

      if (error) throw error;

      addToast('success', 'Sign in link(s) sent successfully');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const alertMessage = useMemo((): ReactNode => {
    return isSingleClient ? (
      <>
        Are you sure you want to send a sign-in link to{' '}
        <strong>{selectedClient?.full_name || 'the client'}</strong>? Their
        password will be reset.
      </>
    ) : (
      <>
        Are you sure you want to send a sign-in link to{' '}
        <strong>{selectedClientIds?.length}</strong> user(s)? Their passwords
        will be reset.
      </>
    );
  }, [isSingleClient, selectedClient, selectedClientIds?.length]);

  const getClientsMap = useCallback(async () => {
    if (!token) return;

    try {
      const usersMap = await getAdminUsersByIds({
        token: token!,
        ids: selectedClientIds,
      });

      setClientsMap(usersMap);
    } catch (error) {
      console.error(error);
    }
  }, [token, selectedClientIds]);

  useEffect(() => {
    if (!isOpen) return;

    getClientsMap();
  }, [isOpen, getClientsMap]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Confirmation">
      <Dialog.Content>
        <Flex gap={4} isColumn>
          <Alert type="warning" icon="warning">
            {alertMessage}
          </Alert>
          <ButtonGroup align="right" isSameLine={true}>
            <Button
              intent="secondary"
              isDisabled={isLoading}
              onClick={onClose}
              value="Cancel"
            />
            <Button
              intent="primary"
              iconLeft="send"
              isDisabled={isLoading}
              onClick={onSendNow}
              value="Send Sign-in Link"
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
